/**
 * Phase 262 — Academy Challenge Leaderboard tile.
 *
 * Dashboard widget — visible to any user (student, coach, owner).  Shows the
 * currently-active challenge for the caller's academy plus a top-10
 * leaderboard.  Students (and any other role) can submit their personal best
 * inline; re-submissions upsert via the backend, so the leaderboard updates
 * live without a duplicate row.
 *
 * Honest semantics:
 *   • If no active challenge exists, returns null (no fake "coming soon"
 *     placeholder).
 *   • Server-side validation rejects negative / NaN / Infinity scores — UI
 *     simply surfaces the error verbatim via toast.
 *   • Rank icons (Trophy / Medal / Award) come from lucide-react per
 *     codebase convention; no emoji icons.
 */
import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import {
  Trophy, Medal, Award, Flame, Zap, Loader2,
  Clock, RefreshCw, ChevronRight,
} from 'lucide-react';

function RankBadge({ rank }) {
  if (rank === 1) return <Trophy className="h-4 w-4 text-amber-500" data-testid="rank-1-icon" />;
  if (rank === 2) return <Medal className="h-4 w-4 text-slate-400" data-testid="rank-2-icon" />;
  if (rank === 3) return <Award className="h-4 w-4 text-amber-700" data-testid="rank-3-icon" />;
  return (
    <span className="text-[10px] font-mono font-bold text-slate-500 tabular-nums w-4 text-center">
      {rank}
    </span>
  );
}

function endsInLabel(endsAt) {
  if (!endsAt) return null;
  try {
    const ms = new Date(endsAt).getTime() - Date.now();
    if (ms <= 0) return 'window closing';
    const days = Math.floor(ms / 86_400_000);
    if (days > 0) return `${days}d left`;
    const hrs = Math.floor(ms / 3_600_000);
    if (hrs > 0) return `${hrs}h left`;
    return `<1h left`;
  } catch (_) { return null; }
}

export default function AcademyChallengeTile({ user }) {
  const [challenge, setChallenge] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scoreInput, setScoreInput] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/academy/challenges/active');
      const ch = data?.challenge;
      setChallenge(ch || null);
      if (ch?.id) {
        const board = await api.get(`/academy/challenges/${ch.id}/board`);
        setSubmissions(board?.data?.submissions || []);
      } else {
        setSubmissions([]);
      }
    } catch (_) {
      setChallenge(null);
      setSubmissions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!challenge?.id || submitting) return;
    const parsed = Number(scoreInput);
    if (!Number.isFinite(parsed) || parsed < 0) {
      toast.error('Enter a valid non-negative score.');
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/academy/challenges/${challenge.id}/submit`, { score: parsed });
      toast.success('Score posted to the leaderboard.');
      setScoreInput('');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to submit score.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Card data-testid="academy-challenge-tile-loading">
        <CardContent className="p-5 space-y-3">
          <Skeleton className="h-5 w-56" />
          <Skeleton className="h-24 w-full" />
        </CardContent>
      </Card>
    );
  }

  // Honest empty state — no active challenge → render nothing.  The dashboard
  // grid simply skips this card.
  if (!challenge) return null;

  const endsLabel = endsInLabel(challenge.ends_at);
  const myRow = submissions.find((s) => s.student_id === user?.id);

  return (
    <Card
      className="border-l-4 border-l-amber-500 relative overflow-hidden"
      data-testid="academy-challenge-tile"
    >
      <Trophy className="absolute -right-6 -top-6 h-32 w-32 text-amber-100 dark:text-amber-900/40 pointer-events-none rotate-12" />
      <CardContent className="p-5 space-y-4 relative">
        {/* ─── Header ─── */}
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-1">
            <div className="flex items-center gap-1.5">
              <Flame className="h-3.5 w-3.5 text-amber-500 animate-pulse" />
              <span className="text-[10px] uppercase font-bold text-amber-700 dark:text-amber-300 tracking-wider">
                Active academy challenge
              </span>
            </div>
            <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100">
              {challenge.title}
            </h3>
            {challenge.description && (
              <p className="text-[11px] text-muted-foreground leading-snug max-w-md">
                {challenge.description}
              </p>
            )}
          </div>
          <div className="flex flex-col items-end gap-1 shrink-0">
            {endsLabel && (
              <Badge variant="outline" className="text-[10px] bg-amber-50 dark:bg-amber-900/30 border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-200">
                <Clock className="h-2.5 w-2.5 mr-0.5" /> {endsLabel}
              </Badge>
            )}
            <button
              type="button"
              onClick={load}
              className="text-[10px] text-slate-500 hover:text-slate-900 dark:hover:text-slate-100 flex items-center gap-0.5 transition-colors"
              data-testid="academy-challenge-refresh"
            >
              <RefreshCw className="h-2.5 w-2.5" /> refresh
            </button>
          </div>
        </div>

        {/* ─── Leaderboard ─── */}
        <div className="space-y-1.5 max-h-56 overflow-y-auto pr-1" data-testid="academy-challenge-leaderboard">
          {submissions.length === 0 ? (
            <div className="text-[11px] text-muted-foreground text-center py-3 italic">
              No scores yet. Be the first to post!
            </div>
          ) : (
            submissions.slice(0, 10).map((row) => {
              const isYou = row.student_id === user?.id;
              return (
                <div
                  key={row.id || `${row.student_id}-${row.rank}`}
                  data-testid={`academy-challenge-row-${row.rank}`}
                  className={`flex items-center justify-between gap-3 px-2.5 py-1.5 rounded-md border ${
                    isYou
                      ? 'bg-amber-50/70 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800'
                      : 'bg-white dark:bg-slate-900 border-slate-100 dark:border-slate-800'
                  }`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <RankBadge rank={row.rank} />
                    <span className="text-xs font-medium text-slate-800 dark:text-slate-100 truncate">
                      {row.student_name || '—'}
                      {isYou && (
                        <span className="ml-1 text-[10px] text-amber-700 dark:text-amber-300 font-bold">(you)</span>
                      )}
                    </span>
                  </div>
                  <span className="text-xs font-mono font-bold tabular-nums text-slate-900 dark:text-slate-100 bg-slate-50 dark:bg-slate-800 px-2 py-0.5 rounded border border-slate-200 dark:border-slate-700">
                    {row.score}
                    <span className="text-[10px] text-muted-foreground font-normal ml-1">
                      {row.unit_label}
                    </span>
                  </span>
                </div>
              );
            })
          )}
        </div>

        {/* ─── Score submission ─── */}
        <form onSubmit={handleSubmit} className="pt-2 border-t border-slate-100 dark:border-slate-800 flex items-center gap-2">
          <Input
            type="number" step="any" min="0"
            value={scoreInput}
            onChange={(e) => setScoreInput(e.target.value)}
            placeholder={
              myRow
                ? `Your best: ${myRow.score} ${challenge.unit_label} — beat it?`
                : `Your best score (${challenge.unit_label})`
            }
            className="h-8 text-xs"
            data-testid="academy-challenge-score-input"
          />
          <Button
            type="submit"
            size="sm"
            disabled={submitting || !scoreInput}
            className="bg-amber-500 hover:bg-amber-600 text-white h-8"
            data-testid="academy-challenge-submit-btn"
          >
            {submitting ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
            ) : (
              <Zap className="h-3.5 w-3.5 mr-1" />
            )}
            Post score
          </Button>
        </form>

        {myRow && (
          <div className="text-[10px] text-muted-foreground flex items-center gap-1">
            <ChevronRight className="h-2.5 w-2.5" />
            You're currently ranked <strong className="text-slate-900 dark:text-slate-100">#{myRow.rank}</strong> of {submissions.length}.
          </div>
        )}
      </CardContent>
    </Card>
  );
}
